import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Action } from '@ngrx/store';
import { filter } from 'rxjs';
import { navList } from '../../../../core/router/data/nav-list';
import { PageHeaderData } from '../../../../core/router/router-state-serializer';
import { User, UserPatient } from '../../../../user/models/user';
import { IconButtonComponent } from '../../icon-button/icon-button.component';
import { IconComponent } from '../../icon/icon.component';
import { NavListItem } from '../models/nav-list-item';
import { getNavDispatchAction, getNavRoute, getNavRouteToMatch } from '../utils/nav-item.utils';

@Component({
  selector: 'app-side-nav',
  imports: [
    MatSidenavModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatTooltipModule,
    CommonModule,
    IconComponent,
    IconButtonComponent,
    RouterModule,
  ],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
})
export class SideNavComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private isToggleLocked = false;
  private selectedPatientId: number | null = null;
  @ViewChild('desktopNavSearchInput') searchInput?: ElementRef<HTMLInputElement>;
  @Input() set patientId(selectedPatientId: number | null) {
    this.selectedPatientId = selectedPatientId;
    this.initializePatient(selectedPatientId);
    this.syncExpandedMenuWithActiveRoute();
  }
  @Input() pageHeaderData: PageHeaderData | null = null;
  @Input() set sideNavOpen(value: boolean) {
    this.isSideNavOpen.set(value);
  }
  @Input() isWideScreen = false;
  @Input() set user(value: User | null) {
    this._user = value;
    this.initializePatient(this.selectedPatientId);
  }
  @Output() dispatchAction = new EventEmitter<Action>();
  @Output() sideNavOpenChange = new EventEmitter<boolean>();
  navList: NavListItem[] = navList?.filter((f: NavListItem) => !f.hideFromNav);
  isSideNavOpen = signal(true);
  expandedMenu = signal<string | null>(null);
  searchValue = '';
  patient: UserPatient | null = null;
  private _user: User | null = null;

  ngOnInit(): void {
    this.syncExpandedMenuWithActiveRoute();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncExpandedMenuWithActiveRoute());
  }

  initializePatient(selectedPatientId: number | null): void {
    this.patient =
      this._user?.userPatients.find(p => p.patientId === selectedPatientId) ??
      this._user?.userPatients.find(p => p.isDefault) ??
      null;
  }

  get filteredNavList(): NavListItem[] {
    const searchQuery = this.searchValue.trim().toLowerCase();
    if (!searchQuery) {
      return this.navList;
    }

    return this.navList
      .map(item => {
        const isParentMatch = item.name.toLowerCase().includes(searchQuery);
        const matchingSubMenu =
          item.subMenu?.filter(subMenuItem => subMenuItem.name.toLowerCase().includes(searchQuery)) ?? [];

        if (isParentMatch) {
          return item;
        }

        if (matchingSubMenu.length > 0) {
          return { ...item, subMenu: matchingSubMenu };
        }

        return null;
      })
      .filter((item): item is NavListItem => item !== null);
  }

  get isSearchActive(): boolean {
    return this.searchValue.trim().length > 0;
  }

  menuItemClicked(navListItem: NavListItem): void {
    const hasSubMenu = !!navListItem.subMenu?.length;
    const canNavigate = this.canMenuItemNavigate(navListItem);

    if (hasSubMenu && !canNavigate) {
      this.toggleSubMenu(navListItem.name);
      return;
    }

    if (hasSubMenu && canNavigate) {
      this.expandedMenu.set(navListItem.name);
    }

    this.activateMenuItem(navListItem);
  }

  subMenuItemClicked(navListItem: NavListItem): void {
    this.activateMenuItem(navListItem);
  }

  hasRouteLink(navListItem: NavListItem): boolean {
    return !getNavDispatchAction(navListItem) && !!getNavRoute(navListItem, this.patient?.patientId);
  }

  getRouteLink(navListItem: NavListItem): string | null {
    return getNavRoute(navListItem, this.patient?.patientId);
  }

  onMenuRouteLinkClicked(navListItem: NavListItem): void {
    if (navListItem.subMenu?.length) {
      this.expandedMenu.set(navListItem.name);
    }
  }

  private canMenuItemNavigate(navListItem: NavListItem): boolean {
    const dispatchAction = getNavDispatchAction(navListItem);
    if (dispatchAction) {
      return true;
    }

    return !!getNavRoute(navListItem, this.patient?.patientId);
  }

  private activateMenuItem(navListItem: NavListItem): void {
    const dispatchAction = getNavDispatchAction(navListItem);
    if (dispatchAction) {
      this.dispatchAction.emit(dispatchAction);
      return;
    }

    const route = getNavRoute(navListItem, this.patient?.patientId);
    if (route) {
      this.router.navigate([route]);
    }
  }

  toggleSubMenu(menuName: string): void {
    this.expandedMenu.set(this.expandedMenu() === menuName ? null : menuName);
  }

  isSubMenuExpanded(menuName: string): boolean {
    return this.expandedMenu() === menuName;
  }

  shouldShowSubMenu(navListItem: NavListItem): boolean {
    if (!this.isSideNavOpen() || !navListItem.subMenu?.length) {
      return false;
    }

    return this.isSearchActive || this.isSubMenuExpanded(navListItem.name);
  }

  isItemActive(navListItem: NavListItem): boolean {
    if (!navListItem.route) {
      return !!navListItem.subMenu?.some(item => this.isItemActive(item));
    }

    const currentRoute = this.router.url.split('?')[0];
    const routeToMatch = getNavRouteToMatch(navListItem, this.patient?.patientId);
    if (!routeToMatch) {
      return !!navListItem.subMenu?.some(item => this.isItemActive(item));
    }

    const isCurrentItemActive = currentRoute === routeToMatch || currentRoute.startsWith(`${routeToMatch}/`);
    const isAnySubMenuActive = !!navListItem.subMenu?.some(item => this.isItemActive(item));
    return isCurrentItemActive || isAnySubMenuActive;
  }

  toggleIsSideNavOpen(event?: Event): void {
    event?.stopPropagation();

    if (this.isToggleLocked) {
      return;
    }

    this.isToggleLocked = true;
    this.setSideNavOpen(!this.isSideNavOpen());

    setTimeout(() => {
      this.isToggleLocked = false;
    }, 120);
  }

  openSearch(): void {
    if (!this.isSideNavOpen()) {
      this.setSideNavOpen(true);
    }

    // Wait for the expanded search input to render before focusing it.
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  private setSideNavOpen(isOpen: boolean): void {
    this.isSideNavOpen.set(isOpen);
    this.sideNavOpenChange.emit(isOpen);
  }

  private syncExpandedMenuWithActiveRoute(): void {
    const activeParentMenu = this.navList.find(item => !!item.subMenu?.length && this.isItemActive(item));
    if (activeParentMenu) {
      this.expandedMenu.set(activeParentMenu.name);
    }
  }
}
